import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    LeftOutlined,
    RightOutlined,
    TeamOutlined,
    DeleteOutlined,
    DollarCircleOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    ProfileOutlined,
    ProjectOutlined,
    StarOutlined,
    FileSearchOutlined,
    FundOutlined,
    SettingFilled,
} from "@ant-design/icons";
import { MenuProps } from "antd";
import { Menu, theme, Tooltip } from "antd";
import { OuterContainer } from "../../styles";
import { useSidebar } from "../../context/SidebarContext";
import { useNavigate, useLocation } from "react-router-dom";
import SystemPayRoutes from "../../utils/constants/Routes";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { getEstablishment } from "../../redux/features/user/userAsyncThunk";
import { permissions } from "../../utils/constants/permissions";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { LostStatuses } from "../../types/LostStatuses";
import { FoundStatuses } from "../../types/FoundItemTypes";
import { clearTicketsByStatus, setBoardStatusFilter } from "../../redux/features/board/columnsSlice";
import { BrandImage } from "../Header/styles";
import { ContentLayout, StickySider, SidebarLogoContainer, CollapseButton, Overlay, GlobalSidebarStyles } from "./styles";
import { resetAuth } from "../../redux/features/auth/authSlice";

type MenuItem = Required<MenuProps>["items"][number];

interface SidebarProps {
    children: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
    const { isSidebarVisible, isCollapsed, hideSidebar, setCollapsed: setContextCollapsed } = useSidebar();
    const {
        token: { colorBgContainer },
    } = theme.useToken();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const userRole = useAppSelector(selectUserRole);
    const [current, setCurrent] = useState("");
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const { establishment } = useAppSelector((state) => state.users);
    // Use the collapsed state from context, but maintain local state for UI updates
    const [collapsed, setCollapsed] = useState(isCollapsed);

    // Fetch establishment data for logo on mount
    useEffect(() => {
        if (!establishment?.logoUrl) {
            dispatch(getEstablishment());
        }
    }, []);

    // Sync local state with context state
    useEffect(() => {
        setCollapsed(isCollapsed);
    }, [isCollapsed]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                // On mobile, we only control visibility through the context
                // but keep the sidebar expanded when it's visible
                setCollapsed(false);
                setContextCollapsed(false);
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize(); // Set initial state

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [setContextCollapsed]);

    const hasPermission = useCallback((permissionKey: string) => checkHasPermission(userRole, permissionKey), [userRole]);

    const getActiveKeyFromPath = () => {
        //reports/found/7, /reports/lost/8, /reports/lost/7
        if (location.pathname.startsWith("/reports/found/" + FoundStatuses.Discarded)) {
            return "discarded-found-items";
        }

        // Sort routes by length (longest first) to match more specific routes before generic ones
        const sortedEntries = Object.entries(menuKeyToRouteMap).sort(([_, routeA], [__, routeB]) => {
            return routeB.length - routeA.length;
        });

        // First try to find an exact match or a route that starts with the path
        const activeEntry = sortedEntries.find(([_, route]) => {
            // Handle both exact matches and nested routes
            return location.pathname === route || (route !== "/" && location.pathname.startsWith(`${route}/`));
        });

        if (activeEntry) return activeEntry[0];

        // If no match found, try to match parent paths
        // For example, if we're on /reports/123 but there's no exact match,
        // we should highlight the 'reports' item
        const pathParts = location.pathname.split("/");
        for (let i = pathParts.length; i > 0; i--) {
            const partialPath = pathParts.slice(0, i).join("/");
            const matchingEntry = sortedEntries.find(([_, route]) => route === partialPath || route === `${partialPath}/`);
            if (matchingEntry) return matchingEntry[0];
        }

        return "";
    };

    // Helper function to find parent keys for a given menu item key
    const findParentKeys = (key: string): string[] => {
        const result: string[] = [];

        // Helper function to search through menu items recursively
        const findInItems = (items: MenuItem[], parentKey?: string) => {
            if (!items) return;

            for (const item of items) {
                if (!item) continue;

                // Check if this item has children and the key is in its children
                if ("children" in item && Array.isArray(item.children)) {
                    // Check if the key is directly in the children
                    const hasKey = item.children.some((child) => child && "key" in child && child.key === key);

                    if (hasKey && "key" in item && typeof item.key === "string") {
                        result.push(item.key);
                        return true;
                    }

                    // Recursively check in children
                    const foundInChildren = findInItems(item.children as MenuItem[], "key" in item ? (item.key as string) : undefined);
                    if (foundInChildren && parentKey) {
                        result.push(parentKey);
                        return true;
                    }
                }
            }

            return false;
        };

        findInItems(items);
        return result;
    };

    // Update current key whenever location changes
    useEffect(() => {
        const activeKey = getActiveKeyFromPath();
        setCurrent(activeKey);

        // If the active key is in a submenu, open that submenu
        const parentKeys = findParentKeys(activeKey);
        if (parentKeys.length > 0 && !collapsed) {
            setOpenKeys((prev) => {
                // Add parent keys without duplicates
                const newKeys = Array.from(new Set([...prev, ...parentKeys]));
                return newKeys;
            });
        }
    }, [location, collapsed]);

    // We'll use CSS-only solution for submenu positioning instead of JavaScript

    const menuKeyToRouteMap: { [key: string]: string } = {
        reports: SystemPayRoutes.reports,
        customers: SystemPayRoutes.customers,
        whiteboard: SystemPayRoutes.whiteboard,
        reviews: SystemPayRoutes.reviews,
        category: SystemPayRoutes.category,
        pricingPlan: SystemPayRoutes.pricingPlan,
        establishmentType: SystemPayRoutes.establishmentType,
        "user-role-configuration": SystemPayRoutes.userRoleConfiguration,
        users: SystemPayRoutes.Users,
        "mail-template": SystemPayRoutes.mailTemplate,
        "delete-reason": SystemPayRoutes.deleteReason,
        "permission-key": SystemPayRoutes.permissionKey,
        "profile-page": SystemPayRoutes.profile,
        "keys-and-settings": SystemPayRoutes.keysAndSettings,
        activity: "/activity",
        email: "/email",
        payment: SystemPayRoutes.payment,
        "deleted-lost-items": `${SystemPayRoutes.reports}/deleted`,
        "unpaid": `${SystemPayRoutes.reports}/unpaid`,
        "lf-notified": `${SystemPayRoutes.reports}/lf-notified`,
        "in-process": `${SystemPayRoutes.reports}/in-process`,
        "conversion-tracking": SystemPayRoutes.conversionTracking,
    };

    const handleLogout = async () => {
        dispatch(resetAuth());
        navigate(SystemPayRoutes.base, { replace: true });
    };

    const onClick = (e: any) => {
        const route = menuKeyToRouteMap[e.key];
        if (route) {
            if (e.key === "reports") {
                dispatch(setBoardStatusFilter(null));
            }
            if (["shipped", "discarded-found-items"].includes(e.key)) {
                const status = FoundStatuses.Discarded;
                dispatch(clearTicketsByStatus(status));
            }
            navigate(route);
        }
    };

    const onOpenChange = (keys: string[]) => {
        setOpenKeys(keys);
    };

    const profileRelatedKeys = ["profile-page", "category", "establishmentType", "pricingPlan", "user-role-configuration", "users", "mail-template", "delete-reason", "keys-and-settings", "permission-key", "logout"];
    const logRelatedKeys = ["logs", "activity", "email", "payment"];

    const isMenuKeyActive = (key: string) => {
        if (key === "logs") {
            return logRelatedKeys.includes(current);
        }
        if (key === "profile") {
            return profileRelatedKeys.includes(current);
        }
        return current === key;
    };

    // Get selected keys including parent keys when a child is active
    const getSelectedKeys = () => {
        const keys = [current];
        // If current key is a log-related key, also select the "logs" parent
        if (logRelatedKeys.includes(current)) {
            keys.push("logs");
        }
        // If current key is a profile-related key, also select the "profile" parent
        if (profileRelatedKeys.includes(current)) {
            keys.push("profile");
        }
        return keys;
    };

    const iconColor = (key: string) => (isMenuKeyActive(key) ? "#144075" : "#000000");
    const withIcon = (IconComponent: React.ComponentType<{ style?: React.CSSProperties }>, key: string) => (
        <IconComponent style={{ color: iconColor(key) }} />
    );

    const items: MenuItem[] = [
        {
            label: "Dashboard",
            key: "reports",
            icon: withIcon(AppstoreOutlined, "reports"),
        },
        // In Process — direct shortcut into the column the client works in by default.
        // Matches the InProcess column icon (SettingFilled / cogwheel) used on the board.
        {
            label: "In Process",
            key: "in-process",
            icon: withIcon(SettingFilled, "in-process"),
        },
        hasPermission(permissions.IsLFNotifiedMenuVisible) ? {
            label: "LF Notified",
            key: "lf-notified",
            icon: withIcon(FileSearchOutlined, "lf-notified"),
        } : null,
        (hasPermission(permissions.IsDraftsMenuVisible) || hasPermission(permissions.IsNotPaidMenuVisible)) ? {
            label: "Unpaid",
            key: "unpaid",
            icon: withIcon(DollarCircleOutlined, "unpaid"),
        } : null,
        hasPermission(permissions.IsDeletedLostItemsMenuVisible) ? {
            label: "Deleted Lost Items",
            key: "deleted-lost-items",
            icon: withIcon(DeleteOutlined, "deleted-lost-items"),
        } : null,
        hasPermission(permissions.IsCustomersMenuVisible) ? {
            label: "Customers",
            key: "customers",
            icon: withIcon(TeamOutlined, "customers"),
        } : null,
        hasPermission(permissions.IsConversionTrackingMenuVisible) ? {
            label: "Conversion Tracking",
            key: "conversion-tracking",
            icon: withIcon(FundOutlined, "conversion-tracking"),
        } : null,
        {
            label: "Logs",
            key: "logs",
            icon: withIcon(FileTextOutlined, "logs"),
            children: [
                {
                    label: collapsed ? (
                        "Activity"
                    ) : (
                        <Tooltip title="Activity" placement="right">
                            Activity
                        </Tooltip>
                    ),
                    key: "activity",
                },
                {
                    label: collapsed ? (
                        "Email"
                    ) : (
                        <Tooltip title="Email" placement="right">
                            Email
                        </Tooltip>
                    ),
                    key: "email",
                },
                hasPermission(permissions.IsPaymentLogAllowed)
                    ? {
                        label: collapsed ? (
                            "Payment"
                        ) : (
                            <Tooltip title="Payment" placement="right">
                                Payment
                            </Tooltip>
                        ),
                        key: "payment",
                    }
                    : null,
            ].filter((item): any => !!item),
        },
        hasPermission(permissions.IsWhiteboardMenuVisible) ? {
            label: "White Boards",
            key: "whiteboard",
            icon: withIcon(ProjectOutlined, "whiteboard"),
        } : null,
        hasPermission(permissions.IsReviewMenuVisible) ? {
            label: "Reviews",
            key: "reviews",
            icon: withIcon(StarOutlined, "reviews"),
        } : null,
        {
            label: "Profile",
            className: "test-1",
            key: "profile",
            icon: withIcon(ProfileOutlined, "profile"),
            children: [
                hasPermission(permissions.IsEstablishmentTypeMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Establishment Type"
                        ) : (
                            <Tooltip title="EstablishmentType" placement="right">
                                Establishment Type
                            </Tooltip>
                        ),
                        key: "establishmentType",
                    } : null,
                hasPermission(permissions.IsPricingPlanMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Pricing Plan"
                        ) : (
                            <Tooltip title="Pricing Plan" placement="right">
                                Pricing Plan
                            </Tooltip>
                        ),
                        key: "pricingPlan",
                    }
                    : null,
                hasPermission(permissions.IsCategoryMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Category"
                        ) : (
                            <Tooltip title="Category" placement="right">
                                Category
                            </Tooltip>
                        ),
                        key: "category",
                    }
                    : null,
                hasPermission(permissions.IsUserRoleConfigMenuVisible)
                    ? {
                        label: collapsed ? (
                            "User Role Configuration"
                        ) : (
                            <Tooltip title="User Role Configuration" placement="right">
                                User Role Configuration
                            </Tooltip>
                        ),
                        key: "user-role-configuration",
                    }
                    : null,
                hasPermission(permissions.IsUserMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Users"
                        ) : (
                            <Tooltip title="Users" placement="right">
                                Users
                            </Tooltip>
                        ),
                        key: "users",
                    }
                    : null,
                hasPermission(permissions.IsEmailTemplateMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Email Template"
                        ) : (
                            <Tooltip title="Email Template" placement="right">
                                Email Template
                            </Tooltip>
                        ),
                        key: "mail-template",
                    }
                    : null,
                hasPermission(permissions.IsDeleteLostReasonMenuVisible)
                    ? {
                        label: collapsed ? (
                            "Delete Lost Reason"
                        ) : (
                            <Tooltip title="Delete Lost Reason" placement="right">
                                Delete Lost Reason
                            </Tooltip>
                        ),
                        key: "delete-reason",
                    }
                    : null,
                {
                    label: collapsed ? (
                        "Profile"
                    ) : (
                        <Tooltip title="Profile" placement="right">
                            Profile
                        </Tooltip>
                    ),
                    key: "profile-page",
                },
                hasPermission(permissions.IsKeyAndSettingMenuVisible) && {
                    label: collapsed ? (
                        "Keys and Settings"
                    ) : (
                        <Tooltip title="Keys and Settings" placement="right">
                            Keys and Settings
                        </Tooltip>
                    ),
                    key: "keys-and-settings",
                    onClick: () => {
                        navigate(SystemPayRoutes.keysAndSettings);
                    },
                },
                hasPermission(permissions.IsPermissionConfigMenuVisible) && {
                    label: collapsed ? (
                        "Permission Management"
                    ) : (
                        <Tooltip title="Permission Management" placement="right">
                            Permission Management
                        </Tooltip>
                    ),
                    key: "permission-key",
                    onClick: () => {
                        navigate(SystemPayRoutes.permissionKey);
                    },
                },
                { label: <span style={{ color: "red" }}>Logout</span>, key: "logout", onClick: handleLogout },
            ].filter((item): any => !!item),
        },
    ].filter((item): any => !!item); // Filter out null values

    // Set initial open state for top-level menus
    // React.useEffect(() => {
    //     // Initialize with some default open menus when component mounts
    //     // This will show expanded submenus by default
    //     if (!collapsed) {
    //         // setOpenKeys(['Reports', 'logs']);
    //     }
    // }, []);

    // Toggle sidebar collapse
    const toggleCollapsed = () => {
        const newCollapsedState = !collapsed;
        setCollapsed(newCollapsedState);
        setContextCollapsed(newCollapsedState); // Update the context state

        // Restore open keys when expanding
        if (!newCollapsedState) {
            // setOpenKeys(['Reports', 'logs']);
        }
    };

    // Handle clicks on menu items for mobile
    const handleMenuItemClick = (e: any) => {
        // On mobile, close the sidebar after clicking a menu item
        if (window.innerWidth <= 768) {
            hideSidebar();
        }
        onClick(e);
    };

    const LogoComponent = useMemo(() => {
        if (establishment?.logoUrl) {
            return <BrandImage src={establishment.logoUrl} alt="" />;
        }

        return null;
    }, [establishment?.logoUrl]);

    // Close sidebar when clicking outside on mobile
    const handleOverlayClick = () => {
        if (window.innerWidth <= 768) {
            hideSidebar();
        }
    };

    return (
        <OuterContainer>
            <GlobalSidebarStyles />
            <Overlay visible={isSidebarVisible && window.innerWidth <= 768} onClick={handleOverlayClick} />
            <StickySider width={200} collapsible collapsed={window.innerWidth >= 768 ? collapsed : !isSidebarVisible} trigger={null} style={{ background: colorBgContainer }} collapsedWidth={80}>
                <SidebarLogoContainer onClick={() => navigate(`${SystemPayRoutes.reports}/in-process`)}>
                    {LogoComponent || (
                        <div
                            style={{
                                fontSize: 18,
                                fontWeight: "bold",
                                color: "#0d63ff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {!collapsed ? "Lost & Found" : "L&F"}
                        </div>
                    )}
                </SidebarLogoContainer>
                <div style={{ flex: 1, overflow: "auto", marginBottom: "50px", maxHeight: "calc(100vh - 125px)", WebkitOverflowScrolling: "touch" }}>
                    {collapsed ? (
                        <Menu
                            mode="vertical"
                            selectedKeys={getSelectedKeys()}
                            onClick={handleMenuItemClick}
                            style={{ height: "100%", borderRight: 0, borderColor: "red" }}
                            items={items}
                            className="sidebar-menu sidebar-menu-collapsed"
                            getPopupContainer={() => document.querySelector('.StickySider') || document.body}
                        />
                    ) : (
                        <Menu
                            mode="inline"
                            selectedKeys={getSelectedKeys()}
                            openKeys={openKeys}
                            onOpenChange={onOpenChange}
                            onClick={handleMenuItemClick}
                            style={{ height: "100%", borderRight: 0 }}
                            items={items}
                            className="sidebar-menu"
                            getPopupContainer={() => document.querySelector('.StickySider') || document.body}
                        />
                    )}
                </div>
                <CollapseButton onClick={toggleCollapsed}>{collapsed ? <RightOutlined /> : <LeftOutlined />}</CollapseButton>
            </StickySider>
            <ContentLayout collapsed={collapsed ? true : false}>{children}</ContentLayout>
        </OuterContainer>
    );
};

export default Sidebar;
